import { releaseClientFetch } from '../app/release_client_state.js';
import { openChoiceDialog } from '../ui/dialog_service.js?v=20260817b';

const EDIT_SESSION_STORAGE_KEY = 'ks-studio-order-edit-session-v1';
const EDIT_SESSION_HEARTBEAT_MS = 20 * 1000;
const RELEASED_NOTICE_MS = 3 * 1000;

let initialized = false;
let releasedNoticeTimer = 0;
let statusRequestInFlight = null;
let infoDialogPromise = null;

function createSessionId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `edit_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
}

function readPreviousSessionId() {
  try {
    return String(globalThis.window?.sessionStorage?.getItem(EDIT_SESSION_STORAGE_KEY) || '').trim();
  } catch (_error) {
    return '';
  }
}

const sessionId = createSessionId();
let previousSessionId = readPreviousSessionId();
try {
  globalThis.window?.sessionStorage?.setItem(EDIT_SESSION_STORAGE_KEY, sessionId);
} catch (_error) {
  // Die Sitzung bleibt ohne sessionStorage fuer die aktuelle Seite nutzbar.
}

const state = {
  orderId: null,
  baseVersionNr: null,
  currentVersionNr: null,
  status: 'none',
  blocker: null,
  notice: '',
  checking: false
};

function numericOrderId(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function numericVersion(value) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= 0 ? numeric : null;
}

function blockerDisplayName(blocker = state.blocker) {
  return String(
    blocker?.displayName
    || `${blocker?.firstName || ''} ${blocker?.lastName || ''}`
    || blocker?.loginName
    || 'Unbekannter Benutzer'
  ).trim() || 'Unbekannter Benutzer';
}

function apiUrl(path) {
  return new URL(path, globalThis.window?.location?.origin || 'http://localhost').href;
}

async function readJsonResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false) {
    const error = new Error(payload?.error || payload?.message || `HTTP ${response.status}`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

function clearReleasedNoticeTimer() {
  if (!releasedNoticeTimer) return;
  globalThis.window?.clearTimeout(releasedNoticeTimer);
  releasedNoticeTimer = 0;
}

function scheduleReleasedNoticeHide() {
  clearReleasedNoticeTimer();
  releasedNoticeTimer = globalThis.window?.setTimeout(() => {
    releasedNoticeTimer = 0;
    state.notice = '';
    renderOrderEditingStatus();
  }, RELEASED_NOTICE_MS) || 0;
}

function statusPresentation() {
  if (state.notice === 'released' && state.status === 'owned') {
    return {
      kind: 'released',
      text: 'Blockierung aufgehoben. Speichern wieder möglich.',
      hidden: false
    };
  }
  if (state.status === 'blocked') {
    return {
      kind: 'blocked',
      text: `In Bearbeitung durch ${blockerDisplayName()}`,
      hidden: false
    };
  }
  if (state.status === 'stale') {
    return {
      kind: 'stale',
      text: 'Neuere Zeichnungsversion verfügbar. Speichern nicht möglich.',
      hidden: false
    };
  }
  return { kind: '', text: '', hidden: true };
}

function renderOrderEditingStatus() {
  const target = globalThis.document?.getElementById?.('orderEditingStatus');
  if (!target) return false;
  const presentation = statusPresentation();
  target.hidden = presentation.hidden;
  target.dataset.state = presentation.kind;
  target.classList?.toggle('is-checking', state.checking === true);
  const label = target.querySelector?.('[data-order-editing-label]');
  if (label) label.textContent = presentation.text;
  target.title = presentation.hidden ? '' : `${presentation.text}${presentation.kind === 'blocked' ? ' – Status aktualisieren' : ''}`;
  target.setAttribute?.('aria-label', target.title || presentation.text);
  return true;
}

function infoMessage(kind, blocker = state.blocker) {
  if (kind === 'stale') return 'Neuere Zeichnungsversion verfügbar. Speichern nicht möglich.';
  const name = blockerDisplayName(blocker);
  if (kind === 'save-blocked') {
    return `Speichern nicht möglich, da der Auftrag durch ${name} bearbeitet wird.`;
  }
  if (kind === 'status-unavailable') {
    return 'Der Bearbeitungsstatus konnte nicht geprüft werden. Speichern ist aus Sicherheitsgründen nicht möglich.';
  }
  return `Auftrag ist bereits durch ${name} geöffnet.`;
}

function openEditingInfoDialog(kind, blocker = state.blocker) {
  if (infoDialogPromise) return infoDialogPromise;
  infoDialogPromise = openChoiceDialog({
    title: 'Auftrag in Bearbeitung',
    message: infoMessage(kind, blocker),
    defaultValue: 'ok',
    choices: [{ value: 'ok', label: 'OK', className: 'primary', autofocus: true }]
  }).finally(() => {
    infoDialogPromise = null;
  });
  return infoDialogPromise;
}

function normalizeServerState(serverState = {}) {
  const status = ['owned', 'blocked', 'stale'].includes(serverState?.status)
    ? serverState.status
    : 'none';
  return {
    status,
    blocker: status === 'blocked' ? (serverState.blocker || null) : null,
    currentVersionNr: numericVersion(serverState.currentVersionNr)
  };
}

function applyServerState(serverState = {}, options = {}) {
  const previousStatus = state.status;
  const normalized = normalizeServerState(serverState);
  state.status = normalized.status;
  state.blocker = normalized.blocker;
  state.currentVersionNr = normalized.currentVersionNr ?? state.currentVersionNr;
  state.checking = false;
  if (state.status !== 'owned') {
    state.notice = '';
    clearReleasedNoticeTimer();
  } else if (previousStatus === 'blocked' && options.showReleasedNotice !== false) {
    state.notice = 'released';
    scheduleReleasedNoticeHide();
  }
  previousSessionId = '';
  renderOrderEditingStatus();
  return state.status;
}

function editingSessionRequestFields() {
  return {
    editSessionId: sessionId,
    previousEditSessionId: previousSessionId || undefined,
    expectedVersionNr: state.baseVersionNr
  };
}

function editingSessionLoadQuery() {
  return {
    editSessionId: sessionId,
    previousEditSessionId: previousSessionId || undefined
  };
}

async function postEditingStatus() {
  if (!state.orderId || state.baseVersionNr == null) return null;
  const payload = await releaseClientFetch(apiUrl(`/api/orders/${state.orderId}/edit-session/status`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      previousSessionId: previousSessionId || undefined,
      baseVersionNr: state.baseVersionNr
    })
  }).then(readJsonResponse);
  return payload.editingSession || null;
}

function refreshOrderEditingStatus(options = {}) {
  if (!state.orderId || state.baseVersionNr == null) return state.status === 'owned' || state.status === 'none';
  if (statusRequestInFlight) return statusRequestInFlight;
  state.checking = true;
  renderOrderEditingStatus();
  statusRequestInFlight = (async () => {
    try {
      const serverState = await postEditingStatus();
      const status = applyServerState(serverState, {
        showReleasedNotice: options.showReleasedNotice !== false
      });
      if (options.showDialog === true) {
        if (status === 'blocked') await openEditingInfoDialog('save-blocked');
        if (status === 'stale') await openEditingInfoDialog('stale');
      }
      return status === 'owned';
    } catch (error) {
      state.checking = false;
      renderOrderEditingStatus();
      if (options.showDialog === true) await openEditingInfoDialog('status-unavailable');
      console.warn('Bearbeitungsstatus konnte nicht geprüft werden', error);
      return false;
    } finally {
      statusRequestInFlight = null;
    }
  })();
  return statusRequestInFlight;
}

async function ensureOrderMaySave(options = {}) {
  if (!state.orderId) return true;
  if (state.status === 'stale') {
    if (options.showDialog !== false) await openEditingInfoDialog('stale');
    return false;
  }
  return refreshOrderEditingStatus({
    showDialog: options.showDialog !== false,
    showReleasedNotice: true
  });
}

function releaseSnapshot(snapshot, options = {}) {
  if (!snapshot?.orderId || !snapshot?.sessionId) return Promise.resolve(false);
  const url = apiUrl(`/api/orders/${snapshot.orderId}/edit-session/release`);
  const body = JSON.stringify({ sessionId: snapshot.sessionId });
  if (options.beacon === true && globalThis.navigator?.sendBeacon) {
    const sent = globalThis.navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    return Promise.resolve(sent);
  }
  return fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: options.keepalive === true
  }).then(response => response.ok).catch(() => false);
}

function currentSnapshot() {
  return {
    orderId: state.orderId,
    sessionId,
    status: state.status
  };
}

function clearCurrentContext() {
  state.orderId = null;
  state.baseVersionNr = null;
  state.currentVersionNr = null;
  state.status = 'none';
  state.blocker = null;
  state.notice = '';
  state.checking = false;
  clearReleasedNoticeTimer();
  renderOrderEditingStatus();
}

function leaveCurrentOrder(options = {}) {
  const snapshot = currentSnapshot();
  clearCurrentContext();
  if (snapshot.status !== 'owned') return false;
  return releaseSnapshot(snapshot, options);
}

async function activateOrderEditingSession(options = {}) {
  const nextOrderId = numericOrderId(options.orderId);
  const nextVersion = numericVersion(options.baseVersionNr);
  const previousSnapshot = currentSnapshot();

  state.orderId = nextOrderId;
  state.baseVersionNr = nextVersion;
  state.currentVersionNr = nextVersion;
  state.status = nextOrderId ? 'none' : 'none';
  state.blocker = null;
  state.notice = '';
  clearReleasedNoticeTimer();
  applyServerState(options.editingSession || {}, { showReleasedNotice: false });

  if (previousSnapshot.orderId && previousSnapshot.orderId !== nextOrderId && previousSnapshot.status === 'owned') {
    releaseSnapshot(previousSnapshot).catch(() => false);
  }
  if (options.announceBlocked === true && state.status === 'blocked') {
    await openEditingInfoDialog('open-blocked');
  }
  return state.status;
}

function applySaveSuccess(payload = {}) {
  const orderId = numericOrderId(payload.order?.auftrag_id);
  const versionNr = numericVersion(payload.version_nr ?? payload.versionNr);
  if (!orderId || versionNr == null) return false;
  const previousSnapshot = currentSnapshot();
  state.orderId = orderId;
  state.baseVersionNr = versionNr;
  state.currentVersionNr = versionNr;
  state.status = 'owned';
  state.blocker = null;
  state.notice = '';
  applyServerState(payload.editingSession || { status: 'owned', currentVersionNr: versionNr }, {
    showReleasedNotice: false
  });
  if (previousSnapshot.orderId && previousSnapshot.orderId !== orderId && previousSnapshot.status === 'owned') {
    releaseSnapshot(previousSnapshot).catch(() => false);
  }
  return true;
}

async function handleOrderSaveConflict(error, options = {}) {
  const code = String(error?.payload?.code || '');
  if (!['ORDER_EDITING_BLOCKED', 'ORDER_VERSION_STALE', 'ORDER_EDIT_SESSION_REQUIRED'].includes(code)) return false;
  const serverState = error?.payload?.editingSession || {};
  applyServerState(serverState, { showReleasedNotice: false });
  if (options.showDialog !== false) {
    if (code === 'ORDER_EDITING_BLOCKED') await openEditingInfoDialog('save-blocked', serverState.blocker);
    else if (code === 'ORDER_VERSION_STALE') await openEditingInfoDialog('stale');
    else await openEditingInfoDialog('status-unavailable');
  }
  return true;
}

function isOrderSaveBlocked() {
  return state.status === 'blocked' || state.status === 'stale';
}

function orderEditingState() {
  return {
    ...state,
    blocker: state.blocker ? { ...state.blocker } : null,
    sessionId
  };
}

function initOrderEditingSession() {
  if (initialized) return;
  initialized = true;
  renderOrderEditingStatus();
  globalThis.document?.getElementById?.('orderEditingStatus')?.addEventListener('click', () => {
    refreshOrderEditingStatus({ showDialog: false, showReleasedNotice: true });
  });
  globalThis.window?.setInterval(() => {
    if (state.status !== 'owned') return;
    refreshOrderEditingStatus({ showDialog: false, showReleasedNotice: false });
  }, EDIT_SESSION_HEARTBEAT_MS);
  globalThis.window?.addEventListener?.('pagehide', () => {
    const snapshot = currentSnapshot();
    if (snapshot.status === 'owned') releaseSnapshot(snapshot, { beacon: true, keepalive: true });
  });
}

export {
  EDIT_SESSION_HEARTBEAT_MS,
  RELEASED_NOTICE_MS,
  activateOrderEditingSession,
  applySaveSuccess,
  blockerDisplayName,
  editingSessionLoadQuery,
  editingSessionRequestFields,
  ensureOrderMaySave,
  handleOrderSaveConflict,
  initOrderEditingSession,
  isOrderSaveBlocked,
  leaveCurrentOrder,
  orderEditingState,
  refreshOrderEditingStatus,
  renderOrderEditingStatus,
  statusPresentation
};
